import { DateObject } from '@capsicum/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ReverseAuction } from '../../services';
import { useProduct } from '../useProduct';

type UseProductReverseAuctionProps = {
	getSsuAmount: () => number;
	isOutOfStock: boolean;
	refetchProduct: () => void;
	variantId: string;
};

export const useProductReverseAuction = ({
	getSsuAmount,
	isOutOfStock,
	refetchProduct,
	variantId
}: UseProductReverseAuctionProps) => {
	const raService = useRef<ReverseAuction>();
	const [countDownString, setCountDownString] = useState<string>('--:--');

	const { getRedeemOptions, variant } = useProduct({
		variantId
	});

	const { decreasingPrice, floorPrice, timeInterval } = getRedeemOptions();
	const isTimerFinished = useMemo(() => getSsuAmount() <= Number(floorPrice), [floorPrice, getSsuAmount]);

	const isReverseAuction = useMemo(
		() => variant?.product?.redeemType === 'reverseAuction' && !isTimerFinished,
		[variant, isTimerFinished]
	);

	const refetchData = useCallback(
		(reverseAuctionOptions?: { expectedPrice: number; timeoutDuration?: number }) => {
			const checkForPriceUpdate = (reverseAuctionOptionsNew: {
				expectedPrice: number;
				timeoutDuration?: number;
			}) => {
				if ((reverseAuctionOptionsNew?.timeoutDuration || 1000) >= 60000) {
					return;
				}

				setTimeout(() => {
					if (getSsuAmount() > reverseAuctionOptionsNew.expectedPrice && !isOutOfStock) {
						const timeoutBase: number = reverseAuctionOptionsNew.timeoutDuration || 1000;
						const timeout: number = Math.round(timeoutBase + timeoutBase / 2) * 2;

						setTimeout(() => {
							refetchData({
								expectedPrice: reverseAuctionOptionsNew.expectedPrice,
								timeoutDuration: timeout
							});
						}, timeout);
					}
				}, 500);
			};

			const currentPrice = getSsuAmount();

			if (isReverseAuction && currentPrice !== reverseAuctionOptions?.expectedPrice) {
				refetchProduct();

				checkForPriceUpdate({
					...reverseAuctionOptions,
					expectedPrice: reverseAuctionOptions?.expectedPrice || getSsuAmount()
				});
			}
		},
		[isReverseAuction, refetchProduct, isOutOfStock, getSsuAmount]
	);

	// Cleanup RA, otherwise it will keep running in the background, for other products
	useEffect(() => {
		return () => {
			raService.current?.cancel();
		};
	}, []);

	useEffect(() => {
		if (variant) {
			if (!floorPrice || !timeInterval || !decreasingPrice || !variant?.redeemableAt) {
				return;
			}

			raService.current?.cancel();
			raService.current = new ReverseAuction(
				{
					decreasingPrice: Number(decreasingPrice),
					endingPrice: Number(floorPrice),
					price: getSsuAmount(),
					startingDate: new DateObject(variant?.redeemableAt || ''),
					timeIntervalInSeconds: Number(timeInterval) * 60
				},
				str => {
					setCountDownString(str);
				},
				expectedPrice => {
					// Postpone refetching the product by 5 secs to allow the price to be updated by WS. In case of
					// WS failure, the price will be updated by the refetchData function with 5 seconds
					setTimeout(() => {
						refetchData({ expectedPrice });
					}, 5000);
				}
			);

			raService.current?.start();
		}
	}, [variant, decreasingPrice, floorPrice, timeInterval, getSsuAmount, refetchData]);

	return {
		countDownString,
		isReverseAuction,
		isTimerFinished
	};
};
